import React, { useEffect, useMemo, useState } from "react";
import { Modal, Select, Button, Spin, Alert } from "antd";
import RequestApi from "../api/RequestApi";

type CourseTab = {
    text: string;
    title: string;
}

type Course = {
    id: string | number;
    title: string;
    tabs: CourseTab[]
};

type ApiCourse = {
    id: string | number;
    title?: string;
    name?: string;
};

type CoursePickerModalProps = {
    open: boolean;
    onClose: () => void;

    /** Вернём наружу выбранный курс по клику "Скопировать" */
    onCopy: (course: Course) => void;

    /** Можно передать заголовок модалки */
    title?: string;
};

const ROW_HEIGHT = 32; // приблизительная высота строки в antd Select dropdown
const VISIBLE_ROWS = 15;

export function CoursePickerModal({
                                      open,
                                      onClose,
                                      onCopy,
                                      title = "Выбор курса",
                                  }: CoursePickerModalProps) {
    const [courses, setCourses] = useState<Course[]>([]);
    const [selectedId, setSelectedId] = useState<string | number | null>(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Загружаем курсы при открытии
    useEffect(() => {
        if (!open) return;

        let aborted = false;

        async function loadCourses() {
            setLoading(true);
            setError(null);

            RequestApi.get('course/all-sorter')
                .then((data) => {

                    const normalized: Course[] = (Array.isArray(data.content.items) ? data.content.items : []).map((c:any) => ({
                        id: c.id,
                        title: '[' + c.code + '] ' + c.title,
                        tabs: c.description_tabs
                    })).filter((c:any) => c.id !== undefined && c.title.trim().length > 0);

                    if (!aborted) {
                        setCourses(normalized);
                        setSelectedId(null);
                    }

                })
                .catch((e: any) => {
                    if (!aborted) setError(e?.message ?? "Ошибка загрузки курсов");
                })
                .finally(() => {
                    if (!aborted) setLoading(false);
                })

        }

        loadCourses();

        return () => {
            aborted = true;
        };
    }, [open]);

    const options = useMemo(
        () =>
            courses.map((c) => ({
                label: c.title,
                value: c.id,
            })),
        [courses]
    );

    const selectedCourse = useMemo(() => {
        if (selectedId === null) return null;
        return courses.find((c) => c.id === selectedId) ?? null;
    }, [selectedId, courses]);

    const handleCopy = () => {
        if (!selectedCourse) return;
        onCopy(selectedCourse);
        onClose();
    };

    return (
        <Modal
            open={open}
            title={title}
            onCancel={onClose}
            footer={null}
            destroyOnClose
        >
            {error ? (
                <Alert type="error" showIcon message="Не удалось загрузить курсы" description={error} />
            ) : null}

            <div style={{ marginTop: 12}}>
                <div>
                    {loading ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <Spin size="small" /> <span>Загружаю курсы…</span>
                        </div>
                    ) : (
                        <Select
                            style={{ width: "100%", marginBottom: 12 }}
                            placeholder="Выберите курс"
                            showSearch
                            optionFilterProp="label"
                            options={options}
                            value={selectedId ?? undefined}
                            onChange={(v) => setSelectedId(v)}
                            // если нужно показывать "not found"
                            notFoundContent="Нет курсов"
                        />
                    )}
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <Button onClick={onClose}>Отмена</Button>
                    <Button
                        type="primary"
                        onClick={handleCopy}
                        disabled={!selectedCourse}
                    >
                        Скопировать
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
